/**
 * KiekkoKingi section controller: store-hero
 * Kept external so the browser can cache this code. The section loads it
 * synchronously after its markup so opening-hour content is resolved before
 * the browser can expose a provisional status.
 */
(() => {
  const kkScript = document.currentScript;
  if (!kkScript) return;

  (() => {
      const sectionId = kkScript.dataset.sectionId;
      const rootId = kkScript.dataset.rootId;
      const initStoreHero = () => {
        const root = document.getElementById(rootId);
        if (!root || root.dataset.storeHeroInitialized === 'true') return;
  
        const configElement = root.querySelector('[data-store-hero-config]');
        if (!configElement) return;
  
        let config;
        try {
          config = JSON.parse(configElement.textContent);
        } catch (error) {
          return;
        }
  
        root.dataset.storeHeroInitialized = 'true';
  
        const primaryStatus = root.querySelector('[data-store-status-primary]');
        const statusDetail = root.querySelector('[data-store-status-detail]');
        const todayStatus = root.querySelector('[data-store-today]');
        const statusRegion = root.querySelector('[data-store-status-region]');
        const video = root.querySelector('[data-store-hero-video]');
        const videoToggle = root.querySelector('[data-store-hero-video-toggle]');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const mobileViewport = window.matchMedia('(max-width: 719px)');
        let userPausedVideo = false;
        let clockTimer;
  
        const parseTime = (value) => {
          const match = typeof value === 'string' && value.trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
          if (!match) return null;
          return Number(match[1]) * 60 + Number(match[2]);
        };
  
        const dayHours = (dayNumber) => {
          const day = config.schedule[String(dayNumber)];
          if (!day || day.closed) return null;
  
          const open = parseTime(day.open);
          const close = parseTime(day.close);
          if (open === null || close === null || close <= open) return null;
  
          return { open, close, label: day.label || '' };
        };
  
        const pad = (value) => String(value).padStart(2, '0');
        const clockTime = (minutes) => `${pad(Math.floor(minutes / 60))}.${pad(minutes % 60)}`;
        const compactTime = (minutes) => minutes % 60 === 0
          ? String(Math.floor(minutes / 60))
          : `${Math.floor(minutes / 60)}.${pad(minutes % 60)}`;
        const hoursRange = (hours) => `${compactTime(hours.open)}–${compactTime(hours.close)}`;
  
        const fillTemplate = (template, values) => String(template || '').replace(/\{([a-z]+)\}/gi, (match, key) => (
          Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
        ));
  
        const helsinkiNow = () => {
          const formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: config.timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
          });
          const parts = {};
          formatter.formatToParts(new Date()).forEach((part) => {
            if (part.type !== 'literal') parts[part.type] = Number(part.value);
          });
          const hour = parts.hour === 24 ? 0 : parts.hour;
          const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
          const nativeDay = date.getUTCDay();
          return {
            year: parts.year,
            month: parts.month,
            day: parts.day,
            weekday: nativeDay === 0 ? 7 : nativeDay,
            minutes: hour * 60 + parts.minute
          };
        };
  
        const nextOpening = (now, skipToday) => {
          for (let offset = 0; offset <= 7; offset += 1) {
            const dayNumber = ((now.weekday - 1 + offset) % 7) + 1;
            const hours = dayHours(dayNumber);
            if (!hours) continue;
            if (offset === 0 && (skipToday || now.minutes >= hours.open)) continue;
  
            let dayLabel = hours.label;
            if (offset === 0) dayLabel = config.strings.todayName;
            if (offset === 1) dayLabel = config.strings.tomorrowName;
  
            return { dayLabel, time: hours.open };
          }
          return null;
        };

        const commitStoreStatus = (state, primary, detail, today) => {
          if (primaryStatus) primaryStatus.textContent = primary;
          if (statusDetail) statusDetail.textContent = detail;
          if (todayStatus) todayStatus.textContent = today;

          // Apply the state last. The loading-state CSS keeps every dynamic
          // opening-hour field hidden until all three values are ready, so a
          // customer never sees a stale closed/open label during hydration.
          root.dataset.storeState = state;
          if (statusRegion) statusRegion.setAttribute('aria-busy', 'false');
        };
  
        const updateStoreStatus = () => {
          let now;
          try {
            now = helsinkiNow();
          } catch (error) {
            // Keep the neutral loading state instead of claiming that the
            // shop is closed when the browser cannot resolve Helsinki time.
            return;
          }
  
          const today = dayHours(now.weekday);
          const automaticOpen = Boolean(today && now.minutes >= today.open && now.minutes < today.close);
          const isOpen = config.manualStatus === 'open'
            ? true
            : config.manualStatus === 'closed'
              ? false
              : automaticOpen;
  
          let detail = config.manualMessage || '';
          if (!detail && isOpen && today) {
            detail = fillTemplate(config.strings.closesTemplate, { time: clockTime(today.close) });
          } else if (!detail && !isOpen) {
            const next = nextOpening(now, config.manualStatus === 'closed');
            if (next) {
              detail = fillTemplate(config.strings.opensTemplate, {
                day: next.dayLabel,
                time: clockTime(next.time)
              });
            }
          }
          let todayText;
          if (config.manualStatus === 'closed') {
            todayText = config.strings.todayClosed;
          } else if (config.manualStatus === 'open' && !today) {
            todayText = config.strings.open;
          } else {
            todayText = today
              ? fillTemplate(config.strings.todayTemplate, { hours: hoursRange(today) })
              : config.strings.todayClosed;
          }

          commitStoreStatus(
            isOpen ? 'open' : 'closed',
            isOpen ? config.strings.open : config.strings.closed,
            detail,
            todayText
          );
        };
  
        const syncVideoToggle = () => {
          if (!video || !videoToggle) return;
          const paused = video.paused;
          videoToggle.dataset.paused = String(paused);
          videoToggle.setAttribute('aria-pressed', String(paused));
        };
  
        const videoIsAvailable = () => Boolean(video && (!mobileViewport.matches || config.useVideoMobile));
  
        const reconcileVideo = () => {
          if (!video) return;
          if (!videoIsAvailable() || reducedMotion.matches || userPausedVideo) {
            video.pause();
            syncVideoToggle();
            return;
          }
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(syncVideoToggle);
          }
        };
  
        const toggleVideo = () => {
          if (!video) return;
          if (video.paused) {
            userPausedVideo = false;
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(syncVideoToggle);
            }
          } else {
            userPausedVideo = true;
            video.pause();
          }
          syncVideoToggle();
        };
  
        const scheduleClockUpdate = () => {
          window.clearTimeout(clockTimer);
          const delay = Math.max(1000, 60000 - (Date.now() % 60000) + 50);
          clockTimer = window.setTimeout(() => {
            updateStoreStatus();
            scheduleClockUpdate();
          }, delay);
        };
  
        updateStoreStatus();
        scheduleClockUpdate();
  
        if (video) {
          video.muted = true;
          video.addEventListener('play', syncVideoToggle);
          video.addEventListener('pause', syncVideoToggle);
          if (videoToggle) videoToggle.addEventListener('click', toggleVideo);
          reducedMotion.addEventListener('change', reconcileVideo);
          mobileViewport.addEventListener('change', reconcileVideo);
          reconcileVideo();
          syncVideoToggle();
        }
  
        root.storeHeroCleanup = () => {
          window.clearTimeout(clockTimer);
          if (video) {
            video.pause();
            video.removeEventListener('play', syncVideoToggle);
            video.removeEventListener('pause', syncVideoToggle);
            reducedMotion.removeEventListener('change', reconcileVideo);
            mobileViewport.removeEventListener('change', reconcileVideo);
          }
          if (videoToggle) videoToggle.removeEventListener('click', toggleVideo);
          delete root.storeHeroCleanup;
          delete root.dataset.storeHeroInitialized;
        };
      };
  
      const sectionMatches = (event) => event.detail && String(event.detail.sectionId) === String(sectionId);
      const handleSectionLoad = (event) => {
        if (sectionMatches(event)) initStoreHero();
      };
      const handleSectionUnload = (event) => {
        if (!sectionMatches(event)) return;
        const root = document.getElementById(rootId);
        if (root && typeof root.storeHeroCleanup === 'function') root.storeHeroCleanup();
        document.removeEventListener('shopify:section:load', handleSectionLoad);
        document.removeEventListener('shopify:section:unload', handleSectionUnload);
      };
  
      initStoreHero();
      document.addEventListener('shopify:section:load', handleSectionLoad);
      document.addEventListener('shopify:section:unload', handleSectionUnload);
    })();
})();
